import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Table, Button, Space, Input, Tag, Badge, Tooltip, Descriptions, message, Modal, Dropdown, Typography } from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  BarChartOutlined,
  HistoryOutlined,
  MoreOutlined,
  CheckCircleFilled,
  MinusCircleFilled,
  ClockCircleOutlined,
  UserOutlined,
  GlobalOutlined,
  TeamOutlined
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import templateService from 'services/templateService';

const { Text } = Typography;

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * TemplateManagementTable - Main table for displaying and managing templates
 */
const TemplateManagementTable = ({
  searchResults,
  filters,
  onEdit,
  onPreview,
  onViewStats,
  onViewHistory,
  onViewOverrides,
  onRefresh,
  selectedTemplates = [],
  onSelectionChange,
}) => {
  const [templates, setTemplates] = useState(searchResults || []);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });

  // Search filter state
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, [filters, tableParams.pagination.current, tableParams.pagination.pageSize]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await templateService.getTemplates({
        ...filters,
        page: tableParams.pagination.current,
        pageSize: tableParams.pagination.pageSize,
      });

      if (response.success) {
        setTemplates(response.data.templates || response.data);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: response.data.total || response.data.length,
          },
        });
      }
    } catch (error) {
      message.error('Failed to load templates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await templateService.deleteTemplate(templateId);
      message.success('Template deleted successfully');
      loadTemplates();
      onRefresh?.();
    } catch (error) {
      message.error('Failed to delete template: ' + error.message);
    }
  };

  const handleClone = async (record) => {
    try {
      const clonedData = {
        ...record,
        templateName: `${record.templateName} (Copy)`,
        isActive: false, // Set to inactive by default
      };
      delete clonedData.templateId;
      delete clonedData.createdDate;
      delete clonedData.lastModifiedDate;

      await templateService.createTemplate(clonedData);
      message.success('Template cloned successfully');
      loadTemplates();
      onRefresh?.();
    } catch (error) {
      message.error('Failed to clone template: ' + error.message);
    }
  };

  // Search filter functionality
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <SearchDropdown onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 12, display: 'block', borderRadius: '8px' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, borderRadius: '6px', background: 'var(--aura-navy)' }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90, borderRadius: '6px' }}
          >
            Reset
          </Button>
        </Space>
      </SearchDropdown>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? 'var(--aura-blue)' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: sorter.order,
      sortField: sorter.field,
    });
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
      sorter: true,
      width: 280,
      ...getColumnSearchProps('templateName'),
      render: (text, record) => (
        <TemplateInfo>
          <TemplateTitle>{text}</TemplateTitle>
          <TemplateMeta>
            {record.isDefault && (
              <DefaultTag color="blue">Default</DefaultTag>
            )}
            <VersionTag>v{record.version || 1}</VersionTag>
          </TemplateMeta>
        </TemplateInfo>
      ),
    },
    {
      title: 'Analysis Level',
      dataIndex: 'analysisLevel',
      key: 'analysisLevel',
      width: 140,
      filters: [
        { text: 'Page', value: 'page' },
        { text: 'Module', value: 'module' },
        { text: 'Dashboard', value: 'dashboard' },
        { text: 'Executive', value: 'executive' },
      ],
      render: (level) => {
        const config = {
          page: { color: '#3a86ff', label: 'Page' },
          module: { color: '#06d6a0', label: 'Module' },
          dashboard: { color: '#ff9f1c', label: 'Dashboard' },
          executive: { color: '#7209b7', label: 'Executive' },
        };
        const item = config[level] || { color: '#8892b0', label: level?.toUpperCase() };
        return (
          <LevelBadge style={{ backgroundColor: item.color + '15', color: item.color, border: `1px solid ${item.color}30` }}>
            {item.label}
          </LevelBadge>
        );
      },
    },
    {
      title: 'SCOPE',
      dataIndex: 'scope',
      key: 'scope',
      width: 120,
      filters: [
        { text: 'System', value: 'SYSTEM' },
        { text: 'Organization', value: 'ORGANIZATION' },
        { text: 'User', value: 'USER' },
      ],
      render: (scope) => {
        const iconMap = {
          SYSTEM: <GlobalOutlined />,
          ORGANIZATION: <TeamOutlined />,
          USER: <UserOutlined />,
        };
        const colorMap = {
          SYSTEM: '#ef476f',
          ORGANIZATION: '#ffd166',
          USER: '#06d6a0',
        };
        return (
          <ScopeTag style={{ color: colorMap[scope] }}>
            {iconMap[scope]}
            <span style={{ marginLeft: 6 }}>{scope}</span>
          </ScopeTag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 110,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      render: (isActive) => (
        <StatusWrapper>
          {isActive ? 
            <CheckCircleFilled style={{ color: '#06d6a0', fontSize: 16 }} /> : 
            <MinusCircleFilled style={{ color: '#e2e8f0', fontSize: 16 }} />
          }
          <StatusText $active={isActive}>{isActive ? 'Active' : 'Inactive'}</StatusText>
        </StatusWrapper>
      ),
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 150,
      sorter: true,
      render: (text) => (
        <DateCell>
          <ClockCircleOutlined style={{ marginRight: 6, opacity: 0.5 }} />
          {formatDate(text)}
        </DateCell>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'clone',
            label: 'Clone Template',
            icon: <CopyOutlined />,
            onClick: () => handleClone(record),
          },
          {
            key: 'stats',
            label: 'Analytics',
            icon: <BarChartOutlined />,
            onClick: () => onViewStats?.(record),
          },
          {
            key: 'history',
            label: 'History',
            icon: <HistoryOutlined />,
            onClick: () => onViewHistory?.(record),
          },
          {
            key: 'overrides',
            label: 'Manage Overrides',
            icon: <GlobalOutlined />,
            onClick: () => onViewOverrides?.(record),
          },
          {
            type: 'divider',
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Delete Template',
                content: `Are you sure you want to delete "${record.templateName}"?`,
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => handleDelete(record.templateId),
              });
            },
          },
        ];

        return (
          <ActionRow size="small">
            <Tooltip title="Preview">
              <IconButton
                icon={<EyeOutlined />}
                onClick={() => onPreview?.(record)}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <IconButton
                icon={<EditOutlined style={{ color: 'var(--aura-blue)' }} />}
                onClick={() => onEdit?.(record)}
              />
            </Tooltip>

            <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
              <IconButton
                icon={<MoreOutlined />}
              />
            </Dropdown>
          </ActionRow>
        );
      },
    },
  ];

  const rowSelection = onSelectionChange ? {
    selectedRowKeys: selectedTemplates.map(t => t.templateId),
    onChange: (selectedRowKeys, selectedRows) => {
      onSelectionChange?.(selectedRows);
    },
  } : undefined;

  return (
    <PremiumTableContainer>
      <StyledTable
        columns={columns}
        dataSource={templates}
        rowKey="templateId"
        pagination={{
          ...tableParams.pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} Intelligence Entities Identified`,
        }}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        rowSelection={rowSelection}
        expandable={{
          expandedRowRender: (record) => (
            <DetailedInsightPanel>
              <InsightTitle>STRATEGIC PARAMETERS & USAGE</InsightTitle>
              <Descriptions bordered size="small" column={3}>
                <Descriptions.Item label="Detailed Description" span={3}>
                  <Text type="secondary">{record.description || 'No strategic description provided for this entity.'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="System Complexity">
                  <Badge 
                    count={record.promptDefinition ? `${(JSON.stringify(record.promptDefinition).length / 1000).toFixed(1)}k chars` : '0k chars'} 
                    overflowCount={99999} 
                    style={{ backgroundColor: 'var(--aura-navy)' }} 
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Visual DNA">
                  {record.htmlTemplate ? <Tag color="cyan">STRUCTURED REFERENCE</Tag> : <Tag color="default">AI AUTONOMOUS</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Origin">
                   <Text strong>{record.createdBy || 'SYSTEM CORE'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Total Executions">
                  <Text strong style={{ color: 'var(--aura-blue)' }}>{record.usageCount || 0}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Active Overrides">
                  <Tag color={record.overrideCount > 0 ? 'volcano' : 'default'}>{record.overrideCount || 0} LAYERS</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Internal Category">
                  <Tag color="purple">{record.category || 'GENERAL'}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </DetailedInsightPanel>
          ),
        }}
      />
    </PremiumTableContainer>
  );
};

const PremiumTableContainer = styled.div`
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.03);
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #f8fafc !important;
    font-size: 10px !important;
    font-weight: 800 !important;
    letter-spacing: 1.5px !important;
    color: var(--aura-slate) !important;
    padding: 20px 16px !important;
    border-bottom: 2px solid #f1f5f9 !important;
    
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 16px !important;
    border-bottom: 1px solid #f1f5f9 !important;
  }

  .ant-table-row {
    transition: all 0.3s ease;
    &:hover {
      background-color: rgba(76, 201, 240, 0.02) !important;
    }
  }

  .ant-pagination {
    padding: 20px 24px !important;
    margin: 0 !important;
    border-top: 1px solid #f1f5f9 !important;
  }
`;

const TemplateInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TemplateTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--aura-navy);
  margin-bottom: 6px;
`;

const TemplateMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DefaultTag = styled(Tag)`
  font-size: 8px !important;
  font-weight: 900 !important;
  letter-spacing: 0.5px;
  border-radius: 4px;
  margin: 0;
  background: #e6f1ff !important;
  border: none !important;
  color: var(--aura-blue) !important;
`;

const VersionTag = styled.span`
  font-size: 11px;
  color: var(--aura-slate);
  font-weight: 500;
`;

const LevelBadge = styled.div`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
`;

const ScopeTag = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusText = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: ${props => props.$active ? '#06d6a0' : 'var(--aura-slate)'};
`;

const DateCell = styled.div`
  font-size: 13px;
  color: var(--aura-slate);
  font-weight: 500;
`;

const ActionRow = styled(Space)`
  display: flex;
  justify-content: flex-end;
`;

const IconButton = styled(Button)`
  border: none !important;
  background: transparent !important;
  width: 32px !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 8px !important;
  color: var(--aura-slate) !important;
  
  &:hover {
    background: #f1f5f9 !important;
    color: var(--aura-navy) !important;
  }
`;

const SearchDropdown = styled.div`
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid #f1f5f9;
`;

const DetailedInsightPanel = styled.div`
  padding: 24px;
  background: #f8fafc;
  border-radius: 0 0 24px 24px;
  border-top: 1px solid #e2e8f0;

  .ant-descriptions-bordered .ant-descriptions-item-label {
    background-color: #f1f5f9 !important;
    font-weight: 700 !important;
    font-size: 11px !important;
    color: var(--aura-navy) !important;
    width: 180px;
  }
`;

const InsightTitle = styled.div`
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 2px;
  color: var(--aura-slate);
  margin-bottom: 16px;
`;

export default TemplateManagementTable;

